import { ApiError } from "@carepass/api-client";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router";
import { downloadAdminFile, uploadAdminFile } from "../../app/files";
import { useAdminNavigation, useRestoreAdminListViewport } from "../../app/navigation/useAdminNavigation";
import { useAdminApplication, useAdminSession, useAdminUiStore } from "../../app/providers";
import { pathForAdminEntityScreen } from "../../app/routes";
import { saveBlob } from "../workspace/behavior";
import type { Schemas } from "../workspace/api.generated";
import { PartnerPage } from "../partners/PartnerPage";
import { ContentView } from "./components/ContentView";
import { useContentDetail, useContentList, useContentMutation } from "./queries";
import type { ContentMutation } from "./queries";
import { parseContentStatus, parseContentType } from "./schema";
import type { ContentStatus, ContentType } from "./schema";

function searchFor(type: ContentType, status: ContentStatus | "") {
  const params = new URLSearchParams({ type });
  if (status) params.set("status", status);
  return params;
}

export function ContentPage() {
  const [params] = useSearchParams();
  return params.get("tab") === "banners" ? <PartnerPage surface="contents"/> : <ContentListPage/>;
}

function ContentListPage() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const services = useAdminApplication();
  const session = useAdminSession();
  const rememberListContext = useAdminUiStore((state) => state.rememberListContext);
  const flashNotice = useAdminUiStore((state) => state.flashNotices.contents);
  const consumeFlashNotice = useAdminUiStore((state) => state.consumeFlashNotice);
  const type = parseContentType(searchParams.get("type"));
  const status = parseContentStatus(searchParams.get("status"));
  const list = useContentList({ type, status });
  const [selectedId, setSelectedId] = useState<number>();
  const detail = useContentDetail(selectedId);
  const mutation = useContentMutation();
  const [notice, setNotice] = useState("");
  const [recoveryError, setRecoveryError] = useState("");
  const fileRequests = useRef(new Set<AbortController>());
  const authorities = session.authSession?.authorities ?? [];
  const navigation = useAdminNavigation({
    contextKey: "contents",
    defaultListPath: `/contents?type=${type}`,
    originLabel: "콘텐츠 목록",
  });
  const restorationNotice = useRestoreAdminListViewport("contents", Boolean(list.data));

  useEffect(() => {
    const canonical = searchFor(type, status);
    if (canonical.toString() !== searchParams.toString()) setSearchParams(canonical, { replace: true });
  }, [searchParams, setSearchParams, status, type]);

  useEffect(() => {
    if (!flashNotice) return;
    setNotice(flashNotice);
    consumeFlashNotice("contents");
  }, [consumeFlashNotice, flashNotice]);

  useEffect(() => () => {
    for (const controller of fileRequests.current) controller.abort();
    fileRequests.current.clear();
  }, []);

  const navigateList = (nextType: ContentType, nextStatus: ContentStatus | "") => {
    const params = searchFor(nextType, nextStatus);
    rememberListContext("contents", { path: `${location.pathname}?${params.toString()}`, page: 0, filters: { type: nextType, status: nextStatus } });
    setSelectedId(undefined); setNotice(""); setRecoveryError(""); setSearchParams(params);
    navigation.focusListResults();
  };

  const onMutationComplete = async (input: ContentMutation, result: unknown) => {
    setRecoveryError("");
    if (input.kind === "delete") {
      setSelectedId(undefined);
      const refreshed = await list.refetch();
      if (refreshed.error) setRecoveryError("삭제 응답은 받았지만 목록을 갱신하지 못했습니다. 다시 조회해 주세요.");
      setNotice(`콘텐츠 ID ${input.id}를 삭제했습니다.`);
      return;
    }
    const saved = result as Schemas["ContentPostView"];
    setSelectedId(saved.id);
    const savedType = parseContentType(saved.contentType);
    const nextStatus = status && status !== saved.status ? "" : status;
    if (savedType !== type || nextStatus !== status) {
      const params = searchFor(savedType, nextStatus);
      rememberListContext("contents", { path: `${location.pathname}?${params.toString()}`, page: 0, filters: { type: savedType, status: nextStatus } });
      setSearchParams(params);
    } else {
      const refreshed = await list.refetch();
      if (refreshed.error) setRecoveryError("처리 응답은 받았지만 현재 목록을 갱신하지 못했습니다. 상세의 서버 상태를 기준으로 확인해 주세요.");
    }
    if (input.kind === "create" || input.kind === "update") setNotice(`${saved.title} · 서버 ID ${saved.id}를 임시 저장했습니다. 자동 게시되지 않았습니다.`);
    if (input.kind === "publish") setNotice(`${saved.title}을 게시했습니다. 서버 상태: ${saved.status}`);
    if (input.kind === "archive") setNotice(`${saved.title}을 보관했습니다. 서버 상태: ${saved.status}`);
  };

  const upload = async (file: File) => {
    const controller = new AbortController(); fileRequests.current.add(controller);
    try { return await uploadAdminFile(services, file, "CONTENT", controller.signal); }
    finally { fileRequests.current.delete(controller); }
  };

  const download = useCallback(async (fileAssetId: number) => {
    if (!selectedId) throw new Error("A content must be selected.");
    const controller = new AbortController(); fileRequests.current.add(controller);
    try {
      const blob = await downloadAdminFile(services, fileAssetId, { businessRefType: "CONTENT", businessRefId: selectedId }, controller.signal);
      saveBlob(blob, `content-${selectedId}-attachment-${fileAssetId}`);
    } finally { fileRequests.current.delete(controller); }
  }, [selectedId, services]);

  const detailState = !selectedId ? "idle" : detail.isPending ? "loading"
    : detail.error instanceof ApiError && detail.error.kind === "forbidden" ? "denied"
      : detail.error instanceof ApiError && detail.error.kind === "not-found" ? "not-found"
        : detail.isError ? "error" : "ready";

  return <ContentView
    onBannerTab={() => setSearchParams({ tab: "banners" })}
    mode="list"
    type={type}
    status={status}
    contents={list.data}
    listState={list.isError ? "error" : list.isPending ? "loading" : "ready"}
    selectedId={selectedId}
    detail={detail.data}
    detailState={detailState}
    canManage={authorities.includes("CONTENT_MANAGE")}
    notice={[notice, restorationNotice].filter(Boolean).join(" ")}
    recoveryError={recoveryError}
    onTypeChange={(next) => navigateList(next, status)}
    onStatusChange={(next) => navigateList(type, next)}
    onSelect={(id) => { setSelectedId(id); setNotice(""); setRecoveryError(""); }}
    detailHref={(id) => pathForAdminEntityScreen("27", id)}
    onOpenDetail={(id) => navigation.openDetail(pathForAdminEntityScreen("27", id), `content:${id}:detail`)}
    newHref={`/contents/new?type=${type}`}
    onOpenNew={() => navigation.openDetail(`/contents/new?type=${type}`, "content:new")}
    onRetryList={() => { void list.refetch(); }}
    onRetryDetail={() => { void detail.refetch(); }}
    onMutation={(input) => mutation.mutateAsync(input)}
    onMutationComplete={onMutationComplete}
    onUpload={upload}
    onDownload={download}
  />;
}
